import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Lock, Unlock, UserPlus, Download, CheckCircle, XCircle, Clock, User as UserIcon, RefreshCw, FolderOpen } from 'lucide-react';
import { getGPTs, createGPT, updateGPT, deleteGPT, updateGPTOrder, getPrompts, createPrompt, updatePrompt, deletePrompt, getUsers, createUser, updateUser, deleteUser, getRegistrations, updateRegistration, deleteRegistration, exportRegistrations, getSuggestedPrompts, updateSuggestedPrompt, deleteSuggestedPrompt, approveSuggestedPrompt, getCategories, createCategory, updateCategory, deleteCategory, updateCategoryOrder } from './services/database';
import { GPT, User, Prompt, SuggestedPrompt, Category } from './types';
import { useAuth } from './contexts/AuthContext';

interface AdminPanelProps {
  isVisible: boolean;
  onClose: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ isVisible, onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'gpts' | 'prompts' | 'users' | 'registrations' | 'suggested-prompts' | 'categories'>('gpts');
  
  // Helper function to scroll to top of admin panel
  const scrollToTop = () => {
    const adminPanel = document.querySelector('.admin-panel-content');
    if (adminPanel) {
      adminPanel.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const [gpts, setGpts] = useState<GPT[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [suggestedPrompts, setSuggestedPrompts] = useState<SuggestedPrompt[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editingGPT, setEditingGPT] = useState<GPT | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingSuggestedPrompt, setEditingSuggestedPrompt] = useState<SuggestedPrompt | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isAddingGPT, setIsAddingGPT] = useState(false);
  const [isAddingPrompt, setIsAddingPrompt] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load GPTs from database
        const gpts = await getGPTs();
        setGpts(gpts);
        
        // Load prompts from database
        const prompts = await getPrompts();
        console.log('Loaded prompts:', prompts);
        setPrompts(prompts);
        
        // Load users from database
        const users = await getUsers();
        setUsers(users);
        
        // Load registrations from database
        const regs = await getRegistrations();
        setRegistrations(regs);
        
        // Load suggested prompts from database
        const suggested = await getSuggestedPrompts();
        setSuggestedPrompts(suggested);
        
        // Load categories from database
        const cats = await getCategories();
        setCategories(cats);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    // Load data when admin panel becomes visible
    if (isVisible) {
      loadData();
    }
  }, [isVisible]);

  // Check if current user is admin
  const isAdmin = user?.role === 'admin';

  // Handle admin panel close with refresh if changes were made
  const handleClose = () => {
    if (hasChanges) {
      // Refresh the page to show changes
      window.location.reload();
    } else {
      // Just close the panel
      onClose();
    }
  };

  const addGPT = async (gpt: GPT) => {
    try {
      console.log('addGPT called with:', gpt);
      console.log('GPT Type in addGPT:', gpt.gptType);
      const newGPT = await createGPT({ ...gpt, id: Date.now().toString() });
      console.log('createGPT returned:', newGPT);
      if (newGPT) {
        setGpts([...gpts, newGPT]);
        setIsAddingGPT(false);
        setHasChanges(true); // Mark that changes were made
      }
    } catch (error) {
      console.error('Error adding GPT:', error);
    }
  };

  const handleUpdateGPT = async (updatedGPT: GPT) => {
    try {
      console.log('handleUpdateGPT called with:', updatedGPT);
      
      console.log('Calling updateGPT with:', updatedGPT.id, updatedGPT);
      const updatedGPTResult = await updateGPT(updatedGPT.id, updatedGPT);
      if (updatedGPTResult) {
        console.log('GPT updated successfully:', updatedGPTResult);
        setGpts(gpts.map(gpt => gpt.id === updatedGPT.id ? updatedGPTResult : gpt));
        setEditingGPT(null);
        setHasChanges(true); // Mark that changes were made
      } else {
        console.log('updateGPT returned null');
      }
    } catch (error) {
      console.error('Error updating GPT:', error);
    }
  };

  const handleDeleteGPT = async (id: string) => {
    if (confirm('Are you sure you want to delete this GPT?')) {
      try {
      const success = await deleteGPT(id);
      if (success) {
        setGpts(gpts.filter(gpt => gpt.id !== id));
        setHasChanges(true); // Mark that changes were made
      }
      } catch (error) {
        console.error('Error deleting GPT:', error);
      }
    }
  };

  const handleAddPrompt = async (prompt: Prompt) => {
    try {
      const newPrompt = await createPrompt({ ...prompt, id: Date.now().toString() });
      if (newPrompt) {
        setPrompts([...prompts, newPrompt]);
        setIsAddingPrompt(false);
        setHasChanges(true); // Mark that changes were made
      }
    } catch (error) {
      console.error('Error adding prompt:', error);
    }
  };

  const handleUpdatePrompt = async (updatedPrompt: Prompt) => {
    try {
      const updatedPromptResult = await updatePrompt(updatedPrompt.id, updatedPrompt);
      if (updatedPromptResult) {
        setPrompts(prompts.map(prompt => prompt.id === updatedPrompt.id ? updatedPromptResult : prompt));
        setEditingPrompt(null);
        setHasChanges(true); // Mark that changes were made
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
    }
  };

  const handleDeletePrompt = async (id: string) => {
    if (confirm('Are you sure you want to delete this prompt?')) {
      try {
        const success = await deletePrompt(id);
      if (success) {
        setPrompts(prompts.filter(prompt => prompt.id !== id));
        setHasChanges(true); // Mark that changes were made
      }
      } catch (error) {
        console.error('Error deleting prompt:', error);
      }
    }
  };

  const handleAddUser = async (user: Omit<User, 'id'>) => {
    try {
      const newUser = await createUser({ ...user, id: Date.now().toString() });
      if (newUser) {
        setUsers([...users, newUser]);
        setIsAddingUser(false);
        setHasChanges(true); // Mark that changes were made
      }
    } catch (error) {
      console.error('Error adding user:', error);
    }
  };

  const handleUpdateUser = async (updatedUser: User) => {
    try {
      const updatedUserResult = await updateUser(updatedUser.id, updatedUser);
      if (updatedUserResult) {
        setUsers(users.map(user => user.id === updatedUser.id ? updatedUserResult : user));
        setEditingUser(null);
        setHasChanges(true); // Mark that changes were made
      }
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (confirm('Are you sure you want to delete this user?')) {
      try {
        const success = await deleteUser(id);
      if (success) {
        setUsers(users.filter(user => user.id !== id));
        setHasChanges(true); // Mark that changes were made
      }
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  // Invite Request handlers
  const handleUpdateRegistration = async (id: string, updates: {
    status?: 'active' | 'inactive';
    inviteSent?: boolean;
    selectedForExport?: boolean;
  }) => {
    try {
      const updatedRegistration = await updateRegistration(id, updates);
      if (updatedRegistration) {
        setRegistrations(registrations.map(reg => 
          reg.id === id ? updatedRegistration : reg
        ));
      }
    } catch (error) {
      console.error('Error updating registration:', error);
    }
  };

  const handleDeleteRegistration = async (id: string) => {
    if (confirm('Are you sure you want to delete this registration?')) {
      try {
        const success = await deleteRegistration(id);
        if (success) {
          setRegistrations(registrations.filter(reg => reg.id !== id));
        }
      } catch (error) {
        console.error('Error deleting registration:', error);
      }
    }
  };

  const handleExportRegistrations = async (selectedOnly: boolean = false) => {
    try {
      const csvData = await exportRegistrations(selectedOnly);
      if (csvData) {
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = selectedOnly ? 'selected-registrations.csv' : 'gpt-registrations.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting registrations:', error);
    }
  };

  // Suggested Prompts handlers
  const handleUpdateSuggestedPrompt = async (updatedPrompt: SuggestedPrompt) => {
    try {
      const result = await updateSuggestedPrompt(updatedPrompt.id, updatedPrompt);
      if (result) {
        setSuggestedPrompts(suggestedPrompts.map(prompt => prompt.id === updatedPrompt.id ? result : prompt));
        setEditingSuggestedPrompt(null);
      }
    } catch (error) {
      console.error('Error updating suggested prompt:', error);
    }
  };

  const handleDeleteSuggestedPrompt = async (id: string) => {
    if (confirm('Are you sure you want to delete this suggested prompt?')) {
      try {
        const success = await deleteSuggestedPrompt(id);
        if (success) {
          setSuggestedPrompts(suggestedPrompts.filter(prompt => prompt.id !== id));
        }
      } catch (error) {
        console.error('Error deleting suggested prompt:', error);
      }
    }
  };

  const handleApproveSuggestedPrompt = async (id: string) => {
    try {
      const result = await approveSuggestedPrompt(id);
      if (result) {
        // Remove from suggested prompts and add to regular prompts
        setSuggestedPrompts(suggestedPrompts.filter(prompt => prompt.id !== id));
        // Reload prompts to include the newly approved one
        const updatedPrompts = await getPrompts();
        setPrompts(updatedPrompts);
      }
    } catch (error) {
      console.error('Error approving suggested prompt:', error);
    }
  };

  const handleRefreshData = async () => {
    try {
      // Load GPTs from database
      const gpts = await getGPTs();
      setGpts(gpts);
      
      // Load prompts from database
      const prompts = await getPrompts();
      console.log('Loaded prompts:', prompts);
      setPrompts(prompts);
      
      // Load users from database
      const users = await getUsers();
      setUsers(users);
      
      // Load registrations from database
      const regs = await getRegistrations();
      setRegistrations(regs);
      
      // Load suggested prompts from database
      const suggested = await getSuggestedPrompts();
      setSuggestedPrompts(suggested);
      
      // Load categories from database
      const cats = await getCategories();
      setCategories(cats);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  // Category Management Functions
  const handleAddCategory = async (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newCategory = await createCategory(category);
      if (newCategory) {
        setCategories([...categories, newCategory]);
        setIsAddingCategory(false);
        setHasChanges(true); // Mark that changes were made
      }
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  const handleUpdateCategory = async (updatedCategory: Category) => {
    try {
      const result = await updateCategory(updatedCategory.id, updatedCategory);
      if (result) {
        setCategories(categories.map(cat => cat.id === updatedCategory.id ? result : cat));
        setEditingCategory(null);
        setHasChanges(true); // Mark that changes were made
      }
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      const success = await deleteCategory(id);
      if (success) {
        setCategories(categories.filter(cat => cat.id !== id));
        setHasChanges(true); // Mark that changes were made
      }
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleUpdateCategoryOrder = async (id: string, displayOrder: number) => {
    try {
      const result = await updateCategoryOrder(id, displayOrder);
      if (result) {
        setCategories(categories.map(cat => cat.id === id ? result : cat));
      }
    } catch (error) {
      console.error('Error updating category order:', error);
    }
  };

  if (!isVisible) return null;

  // Check if user is admin, if not show access denied
  if (!isAdmin) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="text-center">
            <Lock className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              You need admin privileges to access this panel.
            </p>
            <p className="text-sm text-gray-500">
              Current user: {user?.name || user?.email || 'Unknown'}
            </p>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-[#21282D] rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-600">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-100">Admin Panel</h2>
            <div className="flex items-center space-x-2 text-sm text-green-400">
              <Unlock className="w-4 h-4" />
              <span>Authenticated as {user?.name || user?.email}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefreshData}
              className="text-gray-400 hover:text-gray-200 p-2 rounded-lg hover:bg-gray-700 transition-colors duration-200"
              title="Refresh data"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-600">
          <button
            onClick={() => setActiveTab('gpts')}
            className={`px-6 py-3 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'gpts'
                ? 'border-b-2 border-red-400 text-red-400'
                : 'text-gray-300 hover:text-gray-100'
            }`}
          >
            GPTs Management
          </button>
          <button
            onClick={() => setActiveTab('prompts')}
            className={`px-6 py-3 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'prompts'
                ? 'border-b-2 border-red-400 text-red-400'
                : 'text-gray-300 hover:text-gray-100'
            }`}
          >
            Prompt Library Management
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'users'
                ? 'border-b-2 border-red-400 text-red-400'
                : 'text-gray-300 hover:text-gray-100'
            }`}
          >
            User Management
          </button>
          <button
            onClick={() => setActiveTab('registrations')}
            className={`px-6 py-3 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'registrations'
                ? 'border-b-2 border-red-400 text-red-400'
                : 'text-gray-300 hover:text-gray-100'
            }`}
          >
            Registrations
          </button>
          <button
            onClick={() => setActiveTab('suggested-prompts')}
            className={`px-6 py-3 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'suggested-prompts'
                ? 'border-b-2 border-red-400 text-red-400'
                : 'text-gray-300 hover:text-gray-100'
            }`}
          >
            Suggested Prompts
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-6 py-3 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'categories'
                ? 'border-b-2 border-red-400 text-red-400'
                : 'text-gray-300 hover:text-gray-100'
            }`}
          >
            Categories
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] admin-panel-content">
                     {activeTab === 'gpts' ? (
             <GPTsManagement
               gpts={gpts}
               setGpts={setGpts}
               onAddGPT={addGPT}
               onUpdateGPT={handleUpdateGPT}
               onDeleteGPT={handleDeleteGPT}
               editingGPT={editingGPT}
               setEditingGPT={setEditingGPT}
               isAddingGPT={isAddingGPT}
               setIsAddingGPT={setIsAddingGPT}
               scrollToTop={scrollToTop}
               categories={categories}
             />
           ) : activeTab === 'prompts' ? (
             <PromptsManagement
               prompts={prompts}
               onAddPrompt={handleAddPrompt}
               onUpdatePrompt={handleUpdatePrompt}
               onDeletePrompt={handleDeletePrompt}
               editingPrompt={editingPrompt}
               setEditingPrompt={setEditingPrompt}
               isAddingPrompt={isAddingPrompt}
               setIsAddingPrompt={setIsAddingPrompt}
               scrollToTop={scrollToTop}
             />
           ) : activeTab === 'registrations' ? (
             <RegistrationsManagement
               registrations={registrations}
               onUpdateRegistration={handleUpdateRegistration}
               onDeleteRegistration={handleDeleteRegistration}
               onExportRegistrations={handleExportRegistrations}
             />
           ) : activeTab === 'suggested-prompts' ? (
             <SuggestedPromptsManagement
               suggestedPrompts={suggestedPrompts}
               onUpdateSuggestedPrompt={handleUpdateSuggestedPrompt}
               onDeleteSuggestedPrompt={handleDeleteSuggestedPrompt}
               onApproveSuggestedPrompt={handleApproveSuggestedPrompt}
               editingSuggestedPrompt={editingSuggestedPrompt}
               setEditingSuggestedPrompt={setEditingSuggestedPrompt}
             />
           ) : activeTab === 'categories' ? (
             <CategoriesManagement
               categories={categories}
               onAddCategory={handleAddCategory}
               onUpdateCategory={handleUpdateCategory}
               onDeleteCategory={handleDeleteCategory}
               onUpdateCategoryOrder={handleUpdateCategoryOrder}
               editingCategory={editingCategory}
               setEditingCategory={setEditingCategory}
               isAddingCategory={isAddingCategory}
               setIsAddingCategory={setIsAddingCategory}
               scrollToTop={scrollToTop}
             />
           ) : (
             <UsersManagement
               users={users}
               onAddUser={handleAddUser}
               onUpdateUser={handleUpdateUser}
               onDeleteUser={handleDeleteUser}
               editingUser={editingUser}
               setEditingUser={setEditingUser}
               isAddingUser={isAddingUser}
               setIsAddingUser={setIsAddingUser}
               scrollToTop={scrollToTop}
             />
           )}
        </div>
      </div>
    </div>
  );
};

// GPTs Management Component
interface GPTsManagementProps {
  gpts: GPT[];
  setGpts: (gpts: GPT[]) => void;
  onAddGPT: (gpt: GPT) => void;
  onUpdateGPT: (gpt: GPT) => void;
  onDeleteGPT: (id: string) => void;
  editingGPT: GPT | null;
  setEditingGPT: (gpt: GPT | null) => void;
  isAddingGPT: boolean;
  setIsAddingGPT: (adding: boolean) => void;
  scrollToTop: () => void;
  categories: Category[];
}

const GPTsManagement: React.FC<GPTsManagementProps> = ({
  gpts,
  setGpts,
  onAddGPT,
  onUpdateGPT,
  onDeleteGPT,
  editingGPT,
  setEditingGPT,
  isAddingGPT,
  setIsAddingGPT,
  scrollToTop,
  categories
}) => {
  const [selectedGPTs, setSelectedGPTs] = useState<Set<string>>(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [newGPT, setNewGPT] = useState<Partial<GPT>>({
    name: '',
    description: '',
    useCases: [],
    tags: [],
    category: '',
    chatGptUrl: '',
    isActive: true,
    gptType: 'general'
  });

  // Separate state for form string values
  const [useCasesString, setUseCasesString] = useState('');
  const [tagsString, setTagsString] = useState('');
  const [editingUseCasesString, setEditingUseCasesString] = useState('');
  const [editingTagsString, setEditingTagsString] = useState('');
  const [gptOrder, setGptOrder] = useState<Record<string, string[]>>({});

  // Group GPTs by category
  const groupedGPTs = gpts.reduce((acc, gpt) => {
    const category = gpt.category || 'uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(gpt);
    return acc;
  }, {} as Record<string, GPT[]>);

  // Apply custom ordering if available
  Object.keys(groupedGPTs).forEach(category => {
    const customOrder = gptOrder[category];
    if (customOrder) {
      const orderedGPTs = customOrder
        .map(id => groupedGPTs[category].find(gpt => gpt.id === id))
        .filter(Boolean) as GPT[];
      
      // Add any GPTs not in the custom order at the end
      const remainingGPTs = groupedGPTs[category].filter(gpt => !customOrder.includes(gpt.id));
      groupedGPTs[category] = [...orderedGPTs, ...remainingGPTs];
    }
  });

  // Category display names
  const categoryNames: Record<string, string> = {
    'pinned': '🌟 Pinned Assistant',
    'strategy': 'Strategy & Planning',
    'creative': 'Creative & Messaging',
    'data': 'Data & Performance',
    'client': 'Client Communication',
    'operations': 'Operations & Productivity',
    'uncategorized': 'Uncategorized'
  };

  const toggleCategory = (category: string) => {
    const newCollapsed = new Set(collapsedCategories);
    if (newCollapsed.has(category)) {
      newCollapsed.delete(category);
    } else {
      newCollapsed.add(category);
    }
    setCollapsedCategories(newCollapsed);
  };

  const handleSelectGPT = (gptId: string) => {
    const newSelected = new Set(selectedGPTs);
    if (newSelected.has(gptId)) {
      newSelected.delete(gptId);
    } else {
      newSelected.add(gptId);
    }
    setSelectedGPTs(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedGPTs.size === gpts.length) {
      setSelectedGPTs(new Set());
    } else {
      setSelectedGPTs(new Set(gpts.map(gpt => gpt.id)));
    }
  };

  const handleSelectCategory = (category: string) => {
    const categoryGPTs = groupedGPTs[category] || [];
    const categoryGPTIds = categoryGPTs.map(gpt => gpt.id);
    const allSelected = categoryGPTIds.every(id => selectedGPTs.has(id));
    
    const newSelected = new Set(selectedGPTs);
    if (allSelected) {
      // Deselect all GPTs in this category
      categoryGPTIds.forEach(id => newSelected.delete(id));
    } else {
      // Select all GPTs in this category
      categoryGPTIds.forEach(id => newSelected.add(id));
    }
    setSelectedGPTs(newSelected);
  };

  const handleBulkToggleActive = async (isActive: boolean) => {
    const selectedGPTList = gpts.filter(gpt => selectedGPTs.has(gpt.id));
    
    for (const gpt of selectedGPTList) {
      const updatedGPT = { ...gpt, isActive };
      await onUpdateGPT(updatedGPT);
    }
    
    setSelectedGPTs(new Set());
  };

  // Reorder functions
  const moveGPTUp = async (category: string, gptId: string) => {
    const categoryGPTs = groupedGPTs[category] || [];
    const currentIndex = categoryGPTs.findIndex(gpt => gpt.id === gptId);
    if (currentIndex > 0) {
      const newOrder = [...categoryGPTs];
      [newOrder[currentIndex], newOrder[currentIndex - 1]] = [newOrder[currentIndex - 1], newOrder[currentIndex]];
      
      // Update the order for this category
      setGptOrder(prev => ({
        ...prev,
        [category]: newOrder.map(gpt => gpt.id)
      }));

      // Save the new order to database
      try {
        // Update all GPTs in the category with their new display_order values
        for (let i = 0; i < newOrder.length; i++) {
          await updateGPTOrder(newOrder[i].id, i);
        }
        
        // Refresh the data to get the updated order
        const updatedGPTs = await getGPTs();
        setGpts(updatedGPTs);
      } catch (error) {
        console.error('Error updating GPT order:', error);
      }
    }
  };

  const moveGPTDown = async (category: string, gptId: string) => {
    const categoryGPTs = groupedGPTs[category] || [];
    const currentIndex = categoryGPTs.findIndex(gpt => gpt.id === gptId);
    if (currentIndex < categoryGPTs.length - 1) {
      const newOrder = [...categoryGPTs];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
      
      // Update the order for this category
      setGptOrder(prev => ({
        ...prev,
        [category]: newOrder.map(gpt => gpt.id)
      }));

      // Save the new order to database
      try {
        // Update all GPTs in the category with their new display_order values
        for (let i = 0; i < newOrder.length; i++) {
          await updateGPTOrder(newOrder[i].id, i);
        }
        
        // Refresh the data to get the updated order
        const updatedGPTs = await getGPTs();
        setGpts(updatedGPTs);
      } catch (error) {
        console.error('Error updating GPT order:', error);
      }
    }
  };

  const handleSaveGPT = () => {
    if (editingGPT) {
      // Process comma-separated values for useCases and tags
      const processedGPT = {
        ...editingGPT,
        useCases: editingUseCasesString.split(',').map((s: string) => s.trim()).filter((s: string) => s),
        tags: editingTagsString.split(',').map((s: string) => s.trim()).filter((s: string) => s)
      };
      console.log('Saving GPT (edit):', processedGPT);
      console.log('GPT Type in edit:', processedGPT.gptType);
      onUpdateGPT(processedGPT);
    } else {
      // Process comma-separated values for new GPT
      const processedNewGPT = {
        ...newGPT,
        useCases: useCasesString.split(',').map((s: string) => s.trim()).filter((s: string) => s),
        tags: tagsString.split(',').map((s: string) => s.trim()).filter((s: string) => s)
      };
      console.log('Saving GPT (new):', processedNewGPT);
      console.log('GPT Type in new:', processedNewGPT.gptType);
      onAddGPT(processedNewGPT as GPT);
    }
    setNewGPT({
      name: '',
      description: '',
      useCases: [],
      tags: [],
      category: '',
      chatGptUrl: '',
      isActive: true,
      gptType: 'general'
    });
    setUseCasesString('');
    setTagsString('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-100">GPTs Management</h3>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={selectedGPTs.size === gpts.length && gpts.length > 0}
              onChange={handleSelectAll}
              className="w-4 h-4 text-blue-600 border-gray-600 rounded focus:ring-blue-500 bg-[#1B2225]"
            />
            <span className="text-sm text-gray-300">Select All</span>
          </div>
        </div>
        <button
          onClick={() => setIsAddingGPT(true)}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add GPT</span>
        </button>
      </div>

      {/* Add/Edit Form */}
      {(isAddingGPT || editingGPT) && (
        <div className="bg-[#21282D] rounded-lg p-6 border border-gray-600">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-100">
              {editingGPT ? 'Edit GPT' : 'Add New GPT'}
            </h4>
            <button
              onClick={() => {
                setIsAddingGPT(false);
                setEditingGPT(null);
                setUseCasesString('');
                setTagsString('');
                setEditingUseCasesString('');
                setEditingTagsString('');
              }}
              className="text-gray-400 hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Name</label>
                             <input
                 type="text"
                 value={editingGPT ? editingGPT.name : (newGPT.name || '')}
                 onChange={(e) => {
                   if (editingGPT) {
                     setEditingGPT({ ...editingGPT, name: e.target.value });
                   } else {
                     setNewGPT({ ...newGPT, name: e.target.value });
                   }
                 }}
                 className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
               />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Category</label>
              <select
                value={editingGPT ? editingGPT.category : (newGPT.category || '')}
                onChange={(e) => {
                  if (editingGPT) {
                    setEditingGPT({ ...editingGPT, category: e.target.value });
                  } else {
                    setNewGPT({ ...newGPT, category: e.target.value });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              >
                <option value="">Select category</option>
                {categories
                  .filter(cat => cat.isActive)
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map(category => (
                    <option key={category.id} value={category.name}>
                      {category.displayName || category.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-100 mb-2">Description</label>
                             <textarea
                 value={editingGPT ? editingGPT.description : (newGPT.description || '')}
                 onChange={(e) => {
                   if (editingGPT) {
                     setEditingGPT({ ...editingGPT, description: e.target.value });
                   } else {
                     setNewGPT({ ...newGPT, description: e.target.value });
                   }
                 }}
                 rows={3}
                 className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
               />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-100 mb-2">ChatGPT URL</label>
                             <input
                 type="url"
                 value={editingGPT ? editingGPT.chatGptUrl : (newGPT.chatGptUrl || '')}
                 onChange={(e) => {
                   if (editingGPT) {
                     setEditingGPT({ ...editingGPT, chatGptUrl: e.target.value });
                   } else {
                     setNewGPT({ ...newGPT, chatGptUrl: e.target.value });
                   }
                 }}
                 className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                 placeholder="https://chat.openai.com/g/g-..."
               />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Use Cases (comma-separated)</label>
                             <input
                 type="text"
                 value={editingGPT ? editingUseCasesString : useCasesString}
                 onChange={(e) => {
                   if (editingGPT) {
                     setEditingUseCasesString(e.target.value);
                   } else {
                     setUseCasesString(e.target.value);
                   }
                 }}
                 className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
               />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Tags (comma-separated)</label>
                             <input
                 type="text"
                 value={editingGPT ? editingTagsString : tagsString}
                 onChange={(e) => {
                   if (editingGPT) {
                     setEditingTagsString(e.target.value);
                   } else {
                     setTagsString(e.target.value);
                   }
                 }}
                 className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
               />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Status</label>
              <div className="flex items-center space-x-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="isActive"
                    checked={editingGPT ? editingGPT.isActive !== false : (newGPT.isActive !== false)}
                    onChange={() => {
                      const isActive = true;
                      editingGPT ? setEditingGPT({ ...editingGPT, isActive }) : setNewGPT({ ...newGPT, isActive });
                    }}
                    className="mr-2 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-100">Active</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="isActive"
                    checked={editingGPT ? editingGPT.isActive === false : (newGPT.isActive === false)}
                    onChange={() => {
                      const isActive = false;
                      editingGPT ? setEditingGPT({ ...editingGPT, isActive }) : setNewGPT({ ...newGPT, isActive });
                    }}
                    className="mr-2 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-100">Inactive</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Pin to Top</label>
              <div className="flex items-center space-x-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingGPT ? editingGPT.isPinned : (newGPT.isPinned || false)}
                    onChange={(e) => {
                      const isPinned = e.target.checked;
                      editingGPT ? setEditingGPT({ ...editingGPT, isPinned }) : setNewGPT({ ...newGPT, isPinned });
                    }}
                    className="mr-2 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-100">Pin this GPT to the top of the dashboard</span>
                </label>
              </div>
            </div>

            {/* GPT Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-100 mb-2">GPT Type</label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="gptType"
                    value="general"
                    checked={(editingGPT ? editingGPT.gptType : newGPT.gptType) === 'general'}
                    onChange={(e) => {
                      const gptType = e.target.value as 'general' | 'client';
                      editingGPT ? setEditingGPT({ ...editingGPT, gptType }) : setNewGPT({ ...newGPT, gptType });
                    }}
                    className="mr-2 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-100">General GPT</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="gptType"
                    value="client"
                    checked={(editingGPT ? editingGPT.gptType : newGPT.gptType) === 'client'}
                    onChange={(e) => {
                      const gptType = e.target.value as 'general' | 'client';
                      editingGPT ? setEditingGPT({ ...editingGPT, gptType }) : setNewGPT({ ...newGPT, gptType });
                    }}
                    className="mr-2 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-100">Client GPT</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setIsAddingGPT(false);
                setEditingGPT(null);
              }}
              className="px-4 py-2 text-gray-300 hover:text-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveGPT}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>Save</span>
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedGPTs.size > 0 && (
        <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-300">
                {selectedGPTs.size} GPT{selectedGPTs.size !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkToggleActive(true)}
                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors duration-200"
              >
                Activate All
              </button>
              <button
                onClick={() => handleBulkToggleActive(false)}
                className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors duration-200"
              >
                Deactivate All
              </button>
              <button
                onClick={() => setSelectedGPTs(new Set())}
                className="text-blue-400 hover:text-blue-300 text-sm transition-colors duration-200"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GPTs List - Organized by Category */}
      <div className="space-y-6">
        {Object.entries(groupedGPTs).map(([category, categoryGPTs]) => {
          const isCollapsed = collapsedCategories.has(category);
          const categoryName = categoryNames[category] || category;
          const categoryCount = categoryGPTs.length;
          
          return (
            <div key={category} className="border border-gray-600 rounded-lg overflow-hidden">
              {/* Category Header */}
              <div className="bg-[#21282D] px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <button 
                      className="text-gray-400 hover:text-gray-300"
                      onClick={() => toggleCategory(category)}
                    >
                      {isCollapsed ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                    <h3 className="text-lg font-semibold text-gray-100">{categoryName}</h3>
                    <span className="px-2 py-1 text-xs font-medium bg-gray-600 text-gray-300 rounded">
                      {categoryCount} GPT{categoryCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={categoryGPTs.every(gpt => selectedGPTs.has(gpt.id))}
                      onChange={() => handleSelectCategory(category)}
                      className="w-4 h-4 text-blue-600 border-gray-600 rounded focus:ring-blue-500 bg-[#1B2225]"
                    />
                    <span className="text-sm text-gray-300">Select All</span>
                  </div>
                </div>
              </div>

              {/* Category Content */}
              {!isCollapsed && (
                <div className="bg-[#1B2225]">
                  {categoryGPTs.map((gpt) => {
                    const isSelected = selectedGPTs.has(gpt.id);
                    
                    return (
                      <div key={gpt.id} className={`border-b border-gray-600 last:border-b-0 p-4 transition-all duration-200 ${
                        isSelected ? 'bg-blue-900/20 border-l-4 border-l-blue-500' : 'hover:bg-[#21282D]'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectGPT(gpt.id)}
                              className="w-4 h-4 text-blue-600 border-gray-600 rounded focus:ring-blue-500 bg-[#1B2225]"
                            />
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="text-lg font-semibold text-gray-100">{gpt.name}</h4>
                              </div>
                              <p className="text-gray-300 text-sm mt-1">{gpt.description}</p>
                              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-400">
                                <span>Use Cases: {gpt.useCases.length}</span>
                                <span>Tags: {gpt.tags.length}</span>
                                <span className={`px-2 py-1 text-xs font-medium rounded ${
                                  gpt.isActive !== false 
                                    ? 'bg-green-900/20 text-green-400' 
                                    : 'bg-gray-600 text-gray-300'
                                }`}>
                                  {gpt.isActive !== false ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {/* Reorder buttons */}
                            <div className="flex flex-col space-y-1">
                              <button
                                onClick={() => moveGPTUp(category, gpt.id)}
                                disabled={categoryGPTs.indexOf(gpt) === 0}
                                className={`p-2 rounded transition-colors duration-200 ${
                                  categoryGPTs.indexOf(gpt) === 0
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                                }`}
                                title="Move Up"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => moveGPTDown(category, gpt.id)}
                                disabled={categoryGPTs.indexOf(gpt) === categoryGPTs.length - 1}
                                className={`p-2 rounded transition-colors duration-200 ${
                                  categoryGPTs.indexOf(gpt) === categoryGPTs.length - 1
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                                }`}
                                title="Move Down"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </div>
                            <button
                              onClick={() => {
                                console.log('Edit button clicked for GPT:', gpt);
                                setEditingGPT(gpt);
                                setEditingUseCasesString(Array.isArray(gpt.useCases) ? gpt.useCases.join(', ') : '');
                                setEditingTagsString(Array.isArray(gpt.tags) ? gpt.tags.join(', ') : '');
                                scrollToTop();
                              }}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                              title="Edit GPT"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => onDeleteGPT(gpt.id)}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                              title="Delete GPT"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Prompts Management Component
interface PromptsManagementProps {
  prompts: Prompt[];
  onAddPrompt: (prompt: Prompt) => void;
  onUpdatePrompt: (prompt: Prompt) => void;
  onDeletePrompt: (id: string) => void;
  editingPrompt: Prompt | null;
  setEditingPrompt: (prompt: Prompt | null) => void;
  isAddingPrompt: boolean;
  setIsAddingPrompt: (adding: boolean) => void;
  scrollToTop: () => void;
}

const PromptsManagement: React.FC<PromptsManagementProps> = ({
  prompts,
  onAddPrompt,
  onUpdatePrompt,
  onDeletePrompt,
  editingPrompt,
  setEditingPrompt,
  isAddingPrompt,
  setIsAddingPrompt,
  scrollToTop
}) => {
  const [newPrompt, setNewPrompt] = useState<Partial<Prompt>>({
    title: '',
    description: '',
    prompt: '',
    category: '',
    tags: []
  });

  const handleSavePrompt = async () => {
    console.log('handleSavePrompt called');
    try {
      if (editingPrompt) {
        console.log('Updating prompt:', editingPrompt);
        await onUpdatePrompt({ ...editingPrompt, ...newPrompt } as Prompt);
      } else {
        console.log('Adding new prompt:', newPrompt);
        await onAddPrompt(newPrompt as Prompt);
      }
      console.log('Prompt saved successfully');
      setNewPrompt({
        title: '',
        description: '',
        prompt: '',
        category: '',
        tags: []
      });
      setIsAddingPrompt(false);
      setEditingPrompt(null);
      console.log('Form closed');
    } catch (error) {
      console.error('Error saving prompt:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-100">Prompt Library Management</h3>
        <button
          onClick={() => setIsAddingPrompt(true)}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Prompt</span>
        </button>
      </div>

      {/* Add/Edit Form */}
      {(isAddingPrompt || editingPrompt) && (
        <div className="bg-[#21282D] rounded-lg p-6 border border-gray-600">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-100">
              {editingPrompt ? 'Edit Prompt' : 'Add New Prompt'}
            </h4>
            <button
              onClick={() => {
                setIsAddingPrompt(false);
                setEditingPrompt(null);
              }}
              className="text-gray-400 hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Title</label>
              <input
                type="text"
                value={editingPrompt?.title || newPrompt.title || ''}
                onChange={(e) => editingPrompt ? setEditingPrompt({ ...editingPrompt, title: e.target.value }) : setNewPrompt({ ...newPrompt, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Category</label>
              <input
                type="text"
                value={editingPrompt?.category || newPrompt.category || ''}
                onChange={(e) => editingPrompt ? setEditingPrompt({ ...editingPrompt, category: e.target.value }) : setNewPrompt({ ...newPrompt, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-100 mb-2">Description</label>
              <textarea
                value={editingPrompt?.description || newPrompt.description || ''}
                onChange={(e) => editingPrompt ? setEditingPrompt({ ...editingPrompt, description: e.target.value }) : setNewPrompt({ ...newPrompt, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-100 mb-2">Prompt Text</label>
              <textarea
                value={editingPrompt?.prompt || newPrompt.prompt || ''}
                onChange={(e) => editingPrompt ? setEditingPrompt({ ...editingPrompt, prompt: e.target.value }) : setNewPrompt({ ...newPrompt, prompt: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono bg-[#1B2225] text-gray-100"
                placeholder="Enter the prompt text here..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Tags (comma-separated)</label>
              <input
                type="text"
                value={editingPrompt?.tags?.join(', ') || newPrompt.tags?.join(', ') || ''}
                onChange={(e) => {
                  const tags = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                  editingPrompt ? setEditingPrompt({ ...editingPrompt, tags }) : setNewPrompt({ ...newPrompt, tags });
                }}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setIsAddingPrompt(false);
                setEditingPrompt(null);
              }}
              className="px-4 py-2 text-gray-300 hover:text-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSavePrompt}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>Save</span>
            </button>
          </div>
        </div>
      )}

      {/* Prompts List */}
      <div className="space-y-4">
        {prompts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No prompts found. Add your first prompt to get started.</p>
          </div>
        ) : (
          prompts.map((prompt) => (
            <div key={prompt.id} className="bg-[#21282D] border border-gray-600 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-100">{prompt.title}</h4>
                  <p className="text-gray-300 text-sm mt-1">{prompt.description}</p>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-400">
                    <span>Category: {prompt.category}</span>
                    <span>Tags: {prompt.tags?.length || 0}</span>
                  </div>
                  <div className="mt-2 text-xs text-gray-400 font-mono bg-[#1B2225] p-2 rounded border border-gray-600">
                    {prompt.prompt?.substring(0, 100) || 'No prompt text'}...
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setEditingPrompt(prompt);
                      scrollToTop();
                    }}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDeletePrompt(prompt.id)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Users Management Component
interface UsersManagementProps {
  users: User[];
  onAddUser: (user: Omit<User, 'id'>) => void;
  onUpdateUser: (user: User) => void;
  onDeleteUser: (id: string) => void;
  editingUser: User | null;
  setEditingUser: (user: User | null) => void;
  isAddingUser: boolean;
  setIsAddingUser: (adding: boolean) => void;
  scrollToTop: () => void;
}

const UsersManagement: React.FC<UsersManagementProps> = ({
  users,
  onAddUser,
  onUpdateUser,
  onDeleteUser,
  editingUser,
  setEditingUser,
  isAddingUser,
  setIsAddingUser,
  scrollToTop
}) => {
  const { changePassword } = useAuth();
  const [newUser, setNewUser] = useState<Partial<User>>({
    email: '',
    name: '',
    role: 'user',
    password: '' // Added password field
  });
  const [showPasswordChange, setShowPasswordChange] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');

  // Reset form when editing user changes
  useEffect(() => {
    if (editingUser) {
      setNewUser({
        email: editingUser.email,
        name: editingUser.name,
        role: editingUser.role,
        password: '' // Don't populate password for security
      });
    } else if (isAddingUser) {
      setNewUser({
        email: '',
        name: '',
        role: 'user',
        password: ''
      });
    }
  }, [editingUser, isAddingUser]);

  const handleSaveUser = () => {
    if (editingUser) {
      // Only update password if a new one was entered
      const updatedUser = {
        ...editingUser,
        ...newUser,
        password: newUser.password || editingUser.password
      };
      onUpdateUser(updatedUser);
    } else {
      onAddUser(newUser as Omit<User, 'id'>);
    }
    setNewUser({
      email: '',
      name: '',
      role: 'user',
      password: ''
    });
  };

  const handlePasswordChange = async (userId: string) => {
    if (newPassword.trim()) {
      try {
        const result = await changePassword(userId, newPassword);
        if (result.success) {
          console.log('Password updated successfully');
          setNewPassword('');
          setShowPasswordChange(null);
        } else {
          console.error('Failed to update password:', result.error);
          alert(result.error || 'Failed to update password');
        }
      } catch (error) {
        console.error('Error changing password:', error);
        alert('Error changing password');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-100">User Management</h3>
        <button
          onClick={() => setIsAddingUser(true)}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <UserPlus className="w-4 h-4" />
          <span>Add User</span>
        </button>
      </div>

      {/* Add/Edit Form */}
      {(isAddingUser || editingUser) && (
        console.log('Rendering edit modal, editingUser:', editingUser, 'isAddingUser:', isAddingUser),
        <div className="bg-[#1B2225] rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-100">
              {editingUser ? 'Edit User' : 'Add New User'}
            </h4>
            <button
              onClick={() => {
                setIsAddingUser(false);
                setEditingUser(null);
                setNewUser({
                  email: '',
                  name: '',
                  role: 'user',
                  password: ''
                });
              }}
              className="text-gray-400 hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Name</label>
              <input
                type="text"
                value={newUser.name || ''}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#21282D] text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Email</label>
              <input
                type="email"
                value={newUser.email || ''}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#21282D] text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Role</label>
              <select
                value={newUser.role || 'user'}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'admin' | 'user' })}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#21282D] text-gray-100"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">
                {editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
              </label>
              <input
                type="password"
                value={newUser.password || ''}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#21282D] text-gray-100"
                placeholder={editingUser ? 'Enter new password' : 'Enter password'}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setIsAddingUser(false);
                setEditingUser(null);
                setNewUser({
                  email: '',
                  name: '',
                  role: 'user',
                  password: ''
                });
              }}
              className="px-4 py-2 text-gray-300 hover:text-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveUser}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>Save</span>
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-[#21282D] border border-gray-600 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-600">
            <thead className="bg-[#1B2225]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#21282D] divide-y divide-gray-600">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-100">{user.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.role === 'admin' 
                        ? 'bg-red-900 text-red-300' 
                        : 'bg-gray-600 text-gray-100'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {user.lastLogin ? (
                      <div>
                        <div>{new Date(user.lastLogin).toLocaleDateString()}</div>
                        <div className="text-xs text-gray-400">{new Date(user.lastLogin).toLocaleTimeString()}</div>
                      </div>
                    ) : (
                      <span className="text-gray-500 italic">Never</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowPasswordChange(showPasswordChange === user.id ? null : user.id)}
                        className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors duration-200"
                        title="Change Password"
                      >
                        <Lock className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          console.log('Edit button clicked for user:', user);
                          setEditingUser(user);
                          scrollToTop();
                        }}
                        className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20 rounded-lg transition-colors duration-200"
                        title="Edit User"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDeleteUser(user.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                        title="Delete User"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#21282D] rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-100">Change Password</h3>
              <button
                onClick={() => {
                  setShowPasswordChange(null);
                  setNewPassword('');
                }}
                className="text-gray-400 hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-100 mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                  placeholder="Enter new password"
                />
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePasswordChange(showPasswordChange)}
                  disabled={!newPassword.trim()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Update Password
                </button>
                <button
                  onClick={() => {
                    setShowPasswordChange(null);
                    setNewPassword('');
                  }}
                  className="px-4 py-2 text-gray-300 hover:text-gray-100 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Registrations Management Component
interface RegistrationsManagementProps {
  registrations: any[];
  onUpdateRegistration: (id: string, updates: {
    status?: 'active' | 'inactive';
    inviteSent?: boolean;
    selectedForExport?: boolean;
  }) => void;
  onDeleteRegistration: (id: string) => void;
  onExportRegistrations: (selectedOnly?: boolean) => void;
}

const RegistrationsManagement: React.FC<RegistrationsManagementProps> = ({
  registrations,
  onUpdateRegistration,
  onDeleteRegistration,
  onExportRegistrations
}) => {
  const [selectedForExport, setSelectedForExport] = useState<Set<string>>(new Set());

  const handleSelectForExport = (id: string) => {
    const newSelected = new Set(selectedForExport);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedForExport(newSelected);
    
    // Update the registration's selectedForExport status
    onUpdateRegistration(id, { selectedForExport: !newSelected.has(id) });
  };

  const handleSelectAllForExport = () => {
    if (selectedForExport.size === registrations.length) {
      // Deselect all
      registrations.forEach(reg => {
        onUpdateRegistration(reg.id, { selectedForExport: false });
      });
      setSelectedForExport(new Set());
    } else {
      // Select all
      registrations.forEach(reg => {
        onUpdateRegistration(reg.id, { selectedForExport: true });
      });
      setSelectedForExport(new Set(registrations.map(reg => reg.id)));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-100">GPT Registrations Management</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onExportRegistrations(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export Selected</span>
          </button>
          <button
            onClick={() => onExportRegistrations(false)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export All</span>
          </button>
        </div>
      </div>

      {/* Registrations Table */}
      <div className="bg-[#1B2225] border border-gray-600 rounded-lg overflow-hidden">
        {registrations.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No registrations found.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-600">
            <thead className="bg-[#21282D]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedForExport.size === registrations.length}
                    onChange={handleSelectAllForExport}
                    className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-[#1B2225]"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Email
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Invite Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Registered
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#1B2225] divide-y divide-gray-600">
              {registrations.map((registration) => (
                <tr key={registration.id} className="hover:bg-[#21282D]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={registration.selectedForExport}
                      onChange={() => handleSelectForExport(registration.id)}
                      className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-[#1B2225]"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-100">
                      {registration.firstName} {registration.lastName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-100">{registration.email}</div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => onUpdateRegistration(registration.id, { inviteSent: !registration.inviteSent })}
                      className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-200 ${
                        registration.inviteSent
                          ? 'bg-green-900/20 text-green-400'
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                    >
                      {registration.inviteSent ? 'Sent' : 'Not Sent'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    {new Date(registration.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => onDeleteRegistration(registration.id)}
                      className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors duration-200"
                      title="Delete Registration"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Suggested Prompts Management Component
interface SuggestedPromptsManagementProps {
  suggestedPrompts: SuggestedPrompt[];
  onUpdateSuggestedPrompt: (prompt: SuggestedPrompt) => void;
  onDeleteSuggestedPrompt: (id: string) => void;
  onApproveSuggestedPrompt: (id: string) => void;
  editingSuggestedPrompt: SuggestedPrompt | null;
  setEditingSuggestedPrompt: (prompt: SuggestedPrompt | null) => void;
}

const SuggestedPromptsManagement: React.FC<SuggestedPromptsManagementProps> = ({
  suggestedPrompts,
  onUpdateSuggestedPrompt,
  onDeleteSuggestedPrompt,
  onApproveSuggestedPrompt,
  editingSuggestedPrompt,
  setEditingSuggestedPrompt
}) => {
  const [editingForm, setEditingForm] = useState<Partial<SuggestedPrompt>>({});

  const handleSaveEdit = () => {
    if (editingSuggestedPrompt && editingForm.title && editingForm.prompt) {
      onUpdateSuggestedPrompt({
        ...editingSuggestedPrompt,
        ...editingForm
      });
      setEditingForm({});
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-900/20 text-yellow-400';
      case 'approved':
        return 'bg-green-900/20 text-green-400';
      case 'rejected':
        return 'bg-red-900/20 text-red-400';
      default:
        return 'bg-gray-600 text-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-100">Suggested Prompts Management</h3>
        <div className="text-sm text-gray-400">
          {suggestedPrompts.length} suggestion{suggestedPrompts.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Summary Statistics */}
      {suggestedPrompts.length > 0 && (
        <div className="bg-[#21282D] border border-gray-600 rounded-lg p-4 mb-6">
          <h4 className="text-sm font-medium text-gray-100 mb-3">Submission Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <UserIcon className="w-4 h-4 text-blue-400" />
              <span className="text-gray-300">
                <span className="font-medium">{new Set(suggestedPrompts.map(p => p.submittedBy)).size}</span> unique contributors
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-yellow-400" />
              <span className="text-gray-300">
                <span className="font-medium">{suggestedPrompts.filter(p => p.status === 'pending').length}</span> pending review
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-gray-300">
                <span className="font-medium">{suggestedPrompts.filter(p => p.status === 'approved').length}</span> approved
              </span>
            </div>
          </div>
          
          {/* Top Contributors */}
          {(() => {
            const contributorCounts = suggestedPrompts.reduce((acc, prompt) => {
              acc[prompt.submittedBy] = (acc[prompt.submittedBy] || 0) + 1;
              return acc;
            }, {} as Record<string, number>);
            
            const topContributors = Object.entries(contributorCounts)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 3);
            
            if (topContributors.length > 0) {
              return (
                <div className="mt-3 pt-3 border-t border-gray-600">
                  <span className="text-xs text-gray-400">Top contributors: </span>
                  {topContributors.map(([name, count], index) => (
                    <span key={name} className="text-xs text-blue-300">
                      {name} ({count}){index < topContributors.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {suggestedPrompts.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-400">No suggested prompts found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {suggestedPrompts.map((prompt) => (
            <div key={prompt.id} className="bg-[#1B2225] border border-gray-600 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-100">{prompt.title}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded flex items-center space-x-1 ${getStatusColor(prompt.status)}`}>
                      {getStatusIcon(prompt.status)}
                      <span>{prompt.status}</span>
                    </span>
                  </div>
                  
                  <p className="text-gray-300 text-sm mb-2">{prompt.description}</p>
                  
                  <div className="flex items-center space-x-4 text-sm mb-3">
                    <span className="text-gray-400">Category: {prompt.category || 'Uncategorized'}</span>
                    <span className="text-gray-400">Submitted: {new Date(prompt.submittedAt || '').toLocaleDateString()}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2 mb-3 p-2 bg-blue-900/10 border border-blue-600/20 rounded-lg">
                    <UserIcon className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-300 font-medium">Suggested by: {prompt.submittedBy}</span>
                  </div>

                  {prompt.tags && prompt.tags.length > 0 && (
                    <div className="flex items-center space-x-2 mb-3">
                      {prompt.tags.map((tag) => (
                        <span key={tag} className="px-2 py-1 text-xs font-medium bg-gray-600 text-gray-300 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="bg-[#21282D] rounded p-3 border border-gray-600">
                    <p className="text-gray-300 text-sm font-mono leading-relaxed">
                      {prompt.prompt}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  {prompt.status === 'pending' && (
                    <button
                      onClick={() => onApproveSuggestedPrompt(prompt.id)}
                      className="p-2 text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded-lg transition-colors duration-200"
                      title="Approve and add to library"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      setEditingSuggestedPrompt(prompt);
                      setEditingForm({
                        title: prompt.title,
                        description: prompt.description,
                        prompt: prompt.prompt,
                        category: prompt.category,
                        tags: prompt.tags
                      });
                    }}
                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors duration-200"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => onDeleteSuggestedPrompt(prompt.id)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingSuggestedPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#21282D] rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-lg font-medium text-gray-100">Edit Suggested Prompt</h4>
              <button
                onClick={() => {
                  setEditingSuggestedPrompt(null);
                  setEditingForm({});
                }}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-100 mb-2">Title</label>
                <input
                  type="text"
                  value={editingForm.title || ''}
                  onChange={(e) => setEditingForm({ ...editingForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-100 mb-2">Description</label>
                <textarea
                  value={editingForm.description || ''}
                  onChange={(e) => setEditingForm({ ...editingForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-100 mb-2">Prompt Text</label>
                <textarea
                  value={editingForm.prompt || ''}
                  onChange={(e) => setEditingForm({ ...editingForm, prompt: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100 font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-100 mb-2">Category</label>
                <select
                  value={editingForm.category || ''}
                  onChange={(e) => setEditingForm({ ...editingForm, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                >
                  <option value="">Select category</option>
                  <option value="strategy">Strategy & Planning</option>
                  <option value="creative">Creative & Messaging</option>
                  <option value="data">Data & Performance</option>
                  <option value="client">Client Communication</option>
                  <option value="operations">Operations & Productivity</option>
                  <option value="general">General</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-100 mb-2">Status</label>
                <select
                  value={editingSuggestedPrompt.status}
                  onChange={(e) => onUpdateSuggestedPrompt({ ...editingSuggestedPrompt, status: e.target.value as 'pending' | 'approved' | 'rejected' })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setEditingSuggestedPrompt(null);
                  setEditingForm({});
                }}
                className="px-4 py-2 text-gray-300 hover:text-gray-100 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={!editingForm.title || !editingForm.prompt}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Categories Management Component
interface CategoriesManagementProps {
  categories: Category[];
  onAddCategory: (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateCategory: (category: Category) => void;
  onDeleteCategory: (id: string) => void;
  onUpdateCategoryOrder: (id: string, displayOrder: number) => void;
  editingCategory: Category | null;
  setEditingCategory: (category: Category | null) => void;
  isAddingCategory: boolean;
  setIsAddingCategory: (adding: boolean) => void;
  scrollToTop: () => void;
}

const CategoriesManagement: React.FC<CategoriesManagementProps> = ({
  categories,
  onAddCategory,
  onUpdateCategory,
  onDeleteCategory,
  onUpdateCategoryOrder,
  editingCategory,
  setEditingCategory,
  isAddingCategory,
  setIsAddingCategory,
  scrollToTop
}) => {
  const [newCategory, setNewCategory] = useState<Partial<Category>>({
    name: '',
    displayName: '',
    description: '',
    displayOrder: 0,
    isActive: true
  });

  const handleSaveCategory = () => {
    if (newCategory.name && newCategory.displayName) {
      onAddCategory(newCategory as Omit<Category, 'id' | 'createdAt' | 'updatedAt'>);
      setNewCategory({
        name: '',
        displayName: '',
        description: '',
        displayOrder: 0,
        isActive: true
      });
    }
  };

  const handleSaveEdit = () => {
    if (editingCategory) {
      onUpdateCategory(editingCategory);
    }
  };

  const moveCategoryUp = async (category: Category) => {
    const currentIndex = sortedCategories.findIndex(c => c.id === category.id);
    if (currentIndex > 0) {
      const prevCategory = sortedCategories[currentIndex - 1];
      // Swap orders
      await onUpdateCategoryOrder(category.id, prevCategory.displayOrder);
      await onUpdateCategoryOrder(prevCategory.id, category.displayOrder);
    }
  };

  const moveCategoryDown = async (category: Category) => {
    const currentIndex = sortedCategories.findIndex(c => c.id === category.id);
    if (currentIndex < sortedCategories.length - 1) {
      const nextCategory = sortedCategories[currentIndex + 1];
      // Swap orders
      await onUpdateCategoryOrder(category.id, nextCategory.displayOrder);
      await onUpdateCategoryOrder(nextCategory.id, category.displayOrder);
    }
  };

  // Sort categories by display order
  const sortedCategories = [...categories].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-100">Categories Management</h3>
        <button
          onClick={() => setIsAddingCategory(!isAddingCategory)}
          className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200"
        >
          <Plus className="w-4 h-4" />
          <span>Add Category</span>
        </button>
      </div>

      {/* Add Category Form */}
      {isAddingCategory && (
        <div className="bg-[#1B2225] rounded-lg p-6 border border-gray-600">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-100">Add New Category</h4>
            <button
              onClick={() => setIsAddingCategory(false)}
              className="text-gray-400 hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Name (Internal)</label>
              <input
                type="text"
                value={newCategory.name || ''}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="e.g., strategy, creative, data"
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Display Name</label>
              <input
                type="text"
                value={newCategory.displayName || ''}
                onChange={(e) => setNewCategory({ ...newCategory, displayName: e.target.value })}
                placeholder="e.g., Strategy & Planning"
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-100 mb-2">Description</label>
              <textarea
                value={newCategory.description || ''}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                rows={3}
                placeholder="Describe the purpose of this category"
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Display Order</label>
              <input
                type="number"
                value={newCategory.displayOrder || 0}
                onChange={(e) => setNewCategory({ ...newCategory, displayOrder: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Status</label>
              <select
                value={newCategory.isActive ? 'active' : 'inactive'}
                onChange={(e) => setNewCategory({ ...newCategory, isActive: e.target.value === 'active' })}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setIsAddingCategory(false)}
              className="px-4 py-2 text-gray-300 hover:text-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveCategory}
              disabled={!newCategory.name || !newCategory.displayName}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Add Category
            </button>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="bg-[#1B2225] rounded-lg border border-gray-600">
        <div className="p-4 border-b border-gray-600">
          <h4 className="text-lg font-medium text-gray-100">Categories ({sortedCategories.length})</h4>
        </div>

        <div className="divide-y divide-gray-600">
          {sortedCategories.map((category) => (
            <div key={category.id} className="p-4 hover:bg-gray-700 transition-colors duration-200">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <FolderOpen className="w-5 h-5 text-gray-400" />
                    <div>
                      <h5 className="text-gray-100 font-medium">{category.displayName}</h5>
                      <p className="text-sm text-gray-400">{category.name}</p>
                      {category.description && (
                        <p className="text-sm text-gray-500 mt-1">{category.description}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-400">Order: {category.displayOrder}</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    category.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {category.isActive ? 'Active' : 'Inactive'}
                  </span>
                  
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => moveCategoryUp(category)}
                      className="p-1 text-gray-400 hover:text-gray-200 transition-colors duration-200"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveCategoryDown(category)}
                      className="p-1 text-gray-400 hover:text-gray-200 transition-colors duration-200"
                      title="Move down"
                    >
                      ↓
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      setEditingCategory(category);
                      scrollToTop();
                    }}
                    className="p-2 text-gray-400 hover:text-gray-200 transition-colors duration-200"
                    title="Edit category"
                  >
                    <Edit className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => onDeleteCategory(category.id)}
                    className="p-2 text-red-400 hover:text-red-300 transition-colors duration-200"
                    title="Delete category"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Category Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#1B2225] rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-lg font-medium text-gray-100">Edit Category</h4>
              <button
                onClick={() => setEditingCategory(null)}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-100 mb-2">Name (Internal)</label>
                <input
                  type="text"
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-100 mb-2">Display Name</label>
                <input
                  type="text"
                  value={editingCategory.displayName}
                  onChange={(e) => setEditingCategory({ ...editingCategory, displayName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-100 mb-2">Description</label>
                <textarea
                  value={editingCategory.description}
                  onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-100 mb-2">Display Order</label>
                <input
                  type="number"
                  value={editingCategory.displayOrder}
                  onChange={(e) => setEditingCategory({ ...editingCategory, displayOrder: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-100 mb-2">Status</label>
                <select
                  value={editingCategory.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setEditingCategory({ ...editingCategory, isActive: e.target.value === 'active' })}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditingCategory(null)}
                className="px-4 py-2 text-gray-300 hover:text-gray-100 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={!editingCategory.name || !editingCategory.displayName}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel; 
